import React, {
  createRef, useCallback, useContext, useState,
} from 'react';
import {
  Box, CardContent, Grid, Typography, Card, ButtonProps,
  BoxProps, CardContentProps, useMediaQuery, Link, Tooltip, IconButton,
} from '@mui/material';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import LoginIcon from '@mui/icons-material/Login';
import { styled, useTheme } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { CustomerLoginForm, ICustomerLoginForm } from '../form';
import { ICustomerLoginFormFields } from '../../schemas';
import { AuthService, SessionService } from '../../services';
import { SampleBillImage } from '../bill';
import environment from '../../environments/environment';
import Dialog from '../shared/Dialog';
import { FeatureContext } from '../../FeatureContext';

const LoginWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '50px 16px',
}));

const LoginCardContent = styled(CardContent)<CardContentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
}));

const LoginButton = styled(LoadingButton)<ButtonProps>(({ theme }) => ({
  marginTop: '25px',
}));

function getLink(chunks: any) {
  return (
    <Link
      target="_blank"
      rel="noopener noreferrer"
      href={environment.termsCondsLink}
    >
      {chunks}
    </Link>
  );
}

function CustomerLogin() {
  const loginRef = createRef<ICustomerLoginForm>();
  const intl = useIntl();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState('');

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const [sampleBillDialogOpen, setSampleBillDialogOpen] = React.useState(false);
  const featureContext = useContext(FeatureContext);

  const onSubmit = useCallback(
    async (values: ICustomerLoginFormFields) => {
      try {
        setIsLoading(true);
        setErrorCode('');
        const userRes = await AuthService.authorize(values.accountNumber, values.ssn4, featureContext.IA_LOGIN, featureContext.WI_LOGIN);
        SessionService.signIn(userRes);
        navigate('/plans');
      } catch (err: any) {
        const errCode = err.response?.data.message;
        setErrorCode(errCode);
        window.dataLayer.push({
          event: 'Exception',
          error_text: errCode,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [featureContext.IA_LOGIN, featureContext.WI_LOGIN, navigate],
  );

  return (
    <LoginWrapper sx={{ backgroundColor: 'mainBackground.main' }}>

      <Grid container justifyContent="center" spacing={4} sx={{ maxWidth: 944 }}>
        <Grid item md={12} sm={12} xs={12} display="flex" justifyContent={isLargeScreen ? 'left' : 'center'}>
          <Typography sx={{
            typography: {
              sm: 'h5', xs: 'h5', md: 'h4', lg: 'h4',
            },
          }}
          >
            <FormattedMessage id="find-your-best-rate" />
          </Typography>
        </Grid>
        <Grid item md={5} sm={6} xs={12}>
          <Card sx={{ boxShadow: '0px 3px 6px #00000029' }}>
            <LoginCardContent>
              <Typography variant="body2" color={errorCode ? 'error' : undefined}>
                {errorCode
                  ? <ErrorOutlineOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', display: 'inline-flex', margin: '2px' }} />
                  : null}
                {errorCode ? <FormattedMessage id={errorCode} /> : <FormattedMessage id="customer-login.introduction" />}
              </Typography>
              <div>
                <Grid container direction="row">
                  <Grid item md={11} sm={11} xs={11}>
                    <CustomerLoginForm
                      onSubmit={onSubmit}
                      onSampleBillLinkClick={() => setSampleBillDialogOpen(true)}
                      ref={loginRef}
                    />
                  </Grid>
                  <Grid item md={1} sm={1} xs={1} display="flex" alignItems="flex-end">
                    <Tooltip
                      title={intl.formatMessage({ id: 'customer-login.info-description' })}
                      enterTouchDelay={0}
                      leaveTouchDelay={3000}
                    >
                      <IconButton disableRipple>
                        <InfoRoundedIcon sx={{ color: theme.palette.primary.main }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
                <LoginButton
                  fullWidth
                  variant="contained"
                  loading={isLoading}
                  endIcon={<LoginIcon />}
                  onClick={() => loginRef.current?.handleSubmit()}
                >
                  <FormattedMessage id="customer-login.button-signin" />
                </LoginButton>
              </div>
              <Typography variant="h6">
                <FormattedMessage
                  id="customer-login.terms-conditions"
                  values={{
                    link: (chunks: any) => getLink(chunks),
                  }}
                />
              </Typography>
            </LoginCardContent>
          </Card>
        </Grid>
        {isLargeScreen && (
          <Grid item md={7} xs={12} sm={6} display="flex" alignItems="center">
            <SampleBillImage />
          </Grid>
        )}
      </Grid>
      <Dialog
        open={sampleBillDialogOpen}
        handleClose={() => setSampleBillDialogOpen(false)}
      >
        <SampleBillImage />
      </Dialog>
    </LoginWrapper>
  );
}

export { CustomerLogin };
